import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { CourseLayout } from "@/features/BookingWizard/hooks/useGetCourseLayoutOptions/useGetCourseLayoutOptions";
import { SelectableTile } from "@whoosh/design-system/src/components/controls";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { setCourseLayoutAtom } from "../../../../../../atoms/actions/setCourseLayout/setCourseLayoutAtom";
import { CourseLayoutSelectorAtom } from "../../../../../../atoms/selectors/courseLayoutSelectorAtom/courseLayoutSelectorAtom";
import { CourseLayoutControlNode$key } from "./__generated__/CourseLayoutControlNode.graphql";
import { getLayoutHoleCountString } from "./utils/getLayoutHoleCountString";

type CourseLayoutControlProps = {
  nodeRef: CourseLayoutControlNode$key | null;
};

export const CourseLayoutControl = ({ nodeRef }: CourseLayoutControlProps) => {
  const data = useFragment(CourseLayoutControlFragmentString, nodeRef);

  const currentCourseLayout = useAtomValue(CourseLayoutSelectorAtom);

  const enabledLayouts =
    data?.permittedCourseLayouts?.edges?.flatMap((e) => {
      return e?.node && e?.node?.enabled && e?.node?.memberVisible
        ? [e.node]
        : [];
    }) ?? [];

  const layoutOptions = enabledLayouts
    .map((layout: CourseLayout) => ({
      id: layout.id,
      name: `${layout.name} ${getLayoutHoleCountString(
        layout.code,
        layout.holeCount ?? "NONE"
      )}`,
      holeCount: layout.holeCount,
      code: layout.code,
    }))
    .reverse();

  const setCourseLayout = useSetAtom(setCourseLayoutAtom);

  useEffect(() => {
    const defaultLayout = data?.course?.defaultCourseLayout;

    if (!currentCourseLayout && defaultLayout) {
      setCourseLayout(defaultLayout);
    }
  }, [
    layoutOptions,
    currentCourseLayout,
    setCourseLayout,
    data?.course?.defaultCourseLayout,
  ]);

  return (
    <VStack gap={4} padding={5} paddingBottom={3}>
      <Text variant="mainBaseSemiBold" pb={2}>{t`Holes`}</Text>
      <HStack gap={2}>
        {layoutOptions.map((layout) => (
          <SelectableTile
            align="center"
            isSmall
            flexBasis={1}
            key={layout.id}
            setSelected={() => {
              setCourseLayout(layout);
            }}
            title={layout.name}
            isSelected={layout.code === currentCourseLayout?.code}
          />
        ))}
      </HStack>
    </VStack>
  );
};

export const CourseLayoutControlFragmentString = graphql`
  fragment CourseLayoutControlNode on Node {
    ... on TimeSlot {
      agenda {
        id
        facility {
          type
        }
      }
      course {
        defaultCourseLayout {
          id
          code
          holeCount
          name
        }
      }
      permittedCourseLayouts(memberVisible: true) {
        edges {
          node {
            id
            default
            enabled
            holeCount
            code
            memberVisible
            name
          }
        }
      }
    }
  }
`;
